/**
 * The standard list-page furniture: tabs, search, filters, dates, count, toggle.
 *
 * Every list page in the portal needs the same six controls and every one of
 * them had built its own — different heights, different order, different
 * spacing, a select here and a pill-button row there. The result read as a set
 * of separate tools rather than one application, and moving between Leads,
 * Calls, and Form Entries meant relearning where the search box lives.
 *
 * These components fix the ORDER and the GEOMETRY, not the contents. A page
 * still chooses its own tabs and filters; it does not get to choose that search
 * sits left and the view toggle sits right.
 *
 * Page identity (title + subtitle) is deliberately NOT here. That belongs to the
 * AppShell header via `usePageHeader` — a page rendering its own <h2> is the
 * duplicate-title bug this module exists to prevent.
 */

import type { ReactNode } from 'react';
import { Search } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../lib/utils';

/* --------------------------------------------------------------------- tabs */

export interface ListTab {
  id: string;
  label: string;
  count?: number;
  icon?: ReactNode;
}

/**
 * The one tab strip. Underlined, quiet, full-width rule beneath — matching the
 * record-detail tabs so a tab looks like a tab everywhere in the portal.
 *
 * Tabs SWITCH CONTENT. They never carry actions: a row that mixes "Overview"
 * with "Create Lead" teaches that tabs sometimes do things, and then every tab
 * has to be read carefully before it is clicked.
 */
export function ListTabs({
  tabs,
  active,
  onChange,
  label = 'Views',
}: {
  tabs: ListTab[];
  active: string;
  onChange: (id: string) => void;
  label?: string;
}) {
  if (tabs.length < 2) return null;
  return (
    <nav className="-mb-px flex gap-1 overflow-x-auto border-b border-border" role="tablist" aria-label={label}>
      {tabs.map((tab) => {
        const selected = active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(tab.id)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-[13px] transition-colors',
              selected
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium tabular-nums">
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}

/* ------------------------------------------------------------------ toolbar */

/**
 * The filter row. Fixed layout: search first, then filters, then dates; the
 * record count and the view toggle are pushed to the right edge.
 *
 * The count lives here rather than in a stat tile because "how many am I
 * looking at" is a property of the current filters, and it should move when
 * they do.
 */
export function ListToolbar({
  search,
  filters,
  dates,
  count,
  viewToggle,
}: {
  search?: ReactNode;
  filters?: ReactNode;
  dates?: ReactNode;
  /** e.g. "128 leads". Rendered only when supplied. */
  count?: ReactNode;
  viewToggle?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {search}
      {filters}
      {dates}
      {(count || viewToggle) && (
        <div className="ml-auto flex items-center gap-2">
          {count && (
            <span className="whitespace-nowrap text-[12px] tabular-nums text-muted-foreground">
              {count}
            </span>
          )}
          {viewToggle}
        </div>
      )}
    </div>
  );
}

/** The one search box: 36px tall, icon inside, same placeholder grammar. */
export function SearchField({
  value,
  onChange,
  placeholder = 'Search…',
  label = 'Search records',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
}) {
  return (
    <div className="relative w-full max-w-xs">
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        value={value}
        aria-label={label}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-9 pl-8"
      />
    </div>
  );
}

/** The one dropdown filter. Native select: it is one value out of a short list. */
export function FilterSelect({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  label: string;
}) {
  return (
    <select
      value={value}
      aria-label={label}
      onChange={(event) => onChange(event.target.value)}
      className="h-9 rounded-md border border-border bg-card px-2.5 text-[13px] text-foreground"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export interface DateRange {
  from?: string;
  to?: string;
}

/**
 * Date filtering as presets, not two date pickers.
 *
 * "Last 7 days" is the question people actually ask of a feed; a from/to pair
 * makes them compute the answer first. Custom ranges can be added when a real
 * need for one shows up.
 */
export const DATE_PRESETS: Array<{ value: string; label: string; days?: number }> = [
  { value: 'all', label: 'Any date' },
  { value: '24h', label: 'Last 24 hours', days: 1 },
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
];

/** Resolve a preset to the range a list query should send. */
export function dateRangeFromPreset(preset: string): DateRange {
  const found = DATE_PRESETS.find((entry) => entry.value === preset);
  if (!found?.days) return {};
  return { from: new Date(Date.now() - found.days * 24 * 60 * 60 * 1000).toISOString() };
}

export function DateRangeFilter({
  value,
  onChange,
  label = 'Filter by date',
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
}) {
  return <FilterSelect value={value} onChange={onChange} options={DATE_PRESETS} label={label} />;
}
